import React, { useState, useRef } from "react";
import { ButtonSecondaryV2, SkeletonLoader, colors2, DateRangePicker } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_TAG } from "oolib/dist/v2/components/Typo2";
import { useGetLensMeta } from "../hooks/useGetLensData";
import { useGetDashboardMetrics } from "../hooks/useGetDashboardMetrics";
import { DashboardStatsBar } from "../comps/DashboardStatsBar";
import { AIAnnotationWidget } from "../AIAnnotationWidget";
import { PillDropdown } from "../comps/PillDropdown";
import { OverviewBlock } from "./OverviewBlock";
import { SignalsLensBlock } from "./SignalsLensBlock";
import { TopicsLensBlock } from "./TopicsLensBlock";
import { TeamLensBlock } from "./TeamLensBlock";
import { FeatureHealthBlock } from "./FeatureHealthBlock";
import { SegmentLensBlock } from "./SegmentLensBlock";
import { FALLBACK_PERIOD_OPTIONS } from "./constants";

export function LensesTab({ debug = false }) {
  const [period, setPeriod] = useState(null);
  const [locality, setLocality] = useState(null);
  const [showTransparency, setShowTransparency] = useState(false);
  const [customRange, setCustomRange] = useState([undefined, undefined]);
  const lastValidPeriodRef = useRef(null);

  const { data: meta, isLoading: metaLoading } = useGetLensMeta();
  const { data: metrics } = useGetDashboardMetrics();

  const periodOptions = [
    ...(meta?.periods || FALLBACK_PERIOD_OPTIONS)
      .filter((p) => !p.disabled)
      .map((p) => ({ value: p.value || p.key, display: p.display || p.label })),
    { value: "custom_range", display: "Custom Range" },
  ];

  const selectedPeriod = period
    || periodOptions.find((p) => p.value === meta?.defaultPeriod)
    || periodOptions.find((p) => !p.value?.startsWith("__") && p.value !== "custom_range")
    || FALLBACK_PERIOD_OPTIONS[0];

  const isCustom = selectedPeriod?.value === "custom_range";
  const customPeriodStr = isCustom && customRange[0] && customRange[1] && customRange[1] !== "Ongoing"
    ? `custom_${new Date(customRange[0]).toISOString().slice(0, 10)}_${new Date(customRange[1]).toISOString().slice(0, 10)}`
    : null;

  // Don't trigger refetch when custom is selected but dates aren't chosen yet;
  // keep the last valid period active until both dates are set.
  let effectivePeriodValue;
  if (isCustom) {
    effectivePeriodValue = customPeriodStr || lastValidPeriodRef.current;
  } else {
    effectivePeriodValue = selectedPeriod?.value;
    lastValidPeriodRef.current = effectivePeriodValue;
  }

  const handlePeriodChange = (id, val) => {
    setPeriod(val);
    if (val?.value !== "custom_range") setCustomRange([undefined, undefined]);
  };

  const handleCustomRangeChange = (id, val) => {
    setCustomRange(val);
  };

  const localityOptions = [
    { value: "", display: "All Locations" },
    ...(meta?.localities || []).map((loc) => ({ value: loc, display: loc })),
  ];

  if (metaLoading) {
    return (
      <div>
        <div style={{ display: "flex", borderRadius: "1.2rem", border: `1px solid ${colors2.grey10}`, background: colors2.white, overflow: "hidden" }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ flex: 1, padding: "1.2rem 1.4rem", borderRight: i < 6 ? `1px solid ${colors2.grey10}` : "none" }}>
              <SkeletonLoader style={{ height: 10, width: 80, borderRadius: 4, marginBottom: 10 }} />
              <SkeletonLoader style={{ height: 16, width: 100, borderRadius: 4, marginBottom: 6 }} />
              <SkeletonLoader style={{ height: 10, width: 120, borderRadius: 4 }} />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: "1.4rem", alignItems: "center" }}>
          <SkeletonLoader style={{ height: 14, width: 60, borderRadius: 4 }} />
          <SkeletonLoader style={{ height: 22, width: 100, borderRadius: 4 }} />
          <SkeletonLoader style={{ height: 22, width: 90, borderRadius: 4 }} />
        </div>
        {[1, 2].map((row) => (
          <div key={row} style={{ display: "flex", gap: "1.4rem", marginTop: "1.4rem" }}>
            {[1, 2].map((col) => (
              <div key={col} style={{ flex: 1, minWidth: 0, borderRadius: "1.2rem", border: `1px solid ${colors2.grey10}`, background: colors2.white, padding: "1.6rem", height: "40rem" }}>
                <SkeletonLoader style={{ height: 16, width: 140, borderRadius: 4, marginBottom: 20 }} />
                {[1, 2, 3, 4, 5].map((j) => (
                  <div key={j} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0" }}>
                    <SkeletonLoader style={{ height: 14, width: `${40 + j * 5}%`, borderRadius: 4 }} />
                    <SkeletonLoader style={{ height: 22, width: 60, borderRadius: 12 }} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Stats Bar */}
      <DashboardStatsBar metrics={metrics} />

      {/* Annotate-now widget — moved here from the General tab, sits below the stat bar */}
      <div style={{ marginTop: "1.4rem" }}>
        <AIAnnotationWidget />
      </div>

      {/* Filters — below stats */}
      <div style={{ display: "flex", gap: 8, marginTop: "1.4rem", paddingBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey60 }}>Stats for</UI_BODY_SEMIBOLD_SM_DF>
        <PillDropdown
          value={localityOptions.find((l) => l.value === (locality?.value || "")) || localityOptions[0]}
          onChange={(id, val) => setLocality(val?.value ? val : null)}
          options={localityOptions}
        />
        <PillDropdown
          value={selectedPeriod}
          onChange={handlePeriodChange}
          options={periodOptions}
        />
        {isCustom && (
          <DateRangePicker
            id="lens-period-custom"
            value={customRange}
            onChange={handleCustomRangeChange}
            previousDateOnly
          />
        )}
        {debug && <ButtonSecondaryV2
          onClick={() => setShowTransparency(!showTransparency)}
        > Transparency
        </ButtonSecondaryV2>}
      </div>

      {/* Overview Cards */}
      <OverviewBlock period={effectivePeriodValue} locality={locality?.value || null} showTransparency={showTransparency} debug={debug} />

      {/* Row 1: Signals + Topics */}
      <div style={{ display: "flex", gap: "1.4rem", marginTop: "1.4rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SignalsLensBlock period={effectivePeriodValue} locality={locality?.value || null} showTransparency={showTransparency} meta={meta} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TopicsLensBlock period={effectivePeriodValue} locality={locality?.value || null} />
        </div>
      </div>

      {/* Row 2: Issues + Trending Product Features */}
      <div style={{ display: "flex", gap: "1.4rem", marginTop: "1.4rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TeamLensBlock period={effectivePeriodValue} locality={locality?.value || null} showTransparency={showTransparency} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FeatureHealthBlock period={effectivePeriodValue} locality={locality?.value || null} showTransparency={showTransparency} />
        </div>
      </div>

      {/* Row 3: User Segment, full width */}
      <div style={{ marginTop: "1.4rem" }}>
        <SegmentLensBlock period={effectivePeriodValue} meta={meta} showTransparency={showTransparency} />
      </div>
    </div>
  );
}
